import { FormikErrors } from "formik";
import _ from "lodash";
import { isDecimalNumberFormat } from "src/constants/numberFormat";
import { validateWithZod } from "src/services/formValidations";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import * as QuestionVisibility from "../QuestionVisibility";
import * as AddressQuestion from "./Edit/AddressQuestion";
import * as AnswersOptions from "./Edit/AnswersOptions";
import * as GradeQuestion from "./Edit/GradeQuestion";
import * as PermissionLevel from "./Edit/PermissionLevel";
import * as QuestionConstraints from "./Edit/QuestionContraints";
import * as DateConstraints from "./Edit/QuestionContraints/DateConstraints";
import * as QuestionKey from "./Edit/QuestionKey";
import * as QuestionLink from "./Edit/QuestionLink";
import * as QuestionTitle from "./Edit/QuestionTitle";
import * as QuestionType from "./Edit/QuestionType";
import * as Requirement from "./Edit/Requirement";
import * as Verification from "./Edit/Verification";
import * as ConfigureLocationBoundaries from "./QuestionConfiguration/ConfigureLocationBoundaries";
import { SchoolItem } from "./types";

export function validateForm(
  sectionType: AF.V2.SectionType,
  formTemplate: AF.V2.FormTemplate,
  schools: SchoolItem[] | null
) {
  return async (values: FormikType) => {
    // validate with zod first and return if there are any errors
    let errors: FormikErrors<FormikType> =
      (await validateWithZod(FormikSchema)(values)) ?? {};

    const answerOptionsErrors = AnswersOptions.validateForm(schools, values);

    const verificationErrors = Verification.validateForm(
      formTemplate.verifications ?? [],
      values
    );

    const visibilityErrors = QuestionVisibility.validateForm(
      sectionType,
      formTemplate,
      values
    );
    errors = _.merge(
      errors,
      answerOptionsErrors,
      verificationErrors,
      visibilityErrors
    );

    return errors;
  };
}

export const FormikSchema = QuestionKey.FormikSchema.and(
  QuestionType.FormikSchema
)
  .and(QuestionLink.FormikSchema)
  .and(QuestionTitle.FormikSchema)
  .and(AddressQuestion.FormikSchema)
  .and(AnswersOptions.FormikSchema)
  .and(Verification.FormikSchema)
  .and(Requirement.FormikSchema)
  .and(QuestionConstraints.FormikSchema)
  .and(PermissionLevel.FormikSchema)
  .and(GradeQuestion.FormikSchema)
  .and(QuestionVisibility.FormikSchema)
  .and(ConfigureLocationBoundaries.FormikSchema);

export type FormikType = z.infer<typeof FormikSchema>;

export function toFormikValues(
  sectionType: AF.V2.SectionType,
  question: AF.V2.Question,
  verifications: AF.V2.FormVerification[]
): FormikType {
  return {
    ...AnswersOptions.toFormValue(question),
    ...QuestionConstraints.toFormValue(question),
    ...QuestionVisibility.toFormValue(sectionType, question),
    ...GradeQuestion.toFormValue(question),
    ...QuestionType.toFormValue(question),
    ...PermissionLevel.toFormValue(question),
    ...QuestionLink.toFormValue(question),
    ...Verification.toFormValue(question, verifications),
    ...ConfigureLocationBoundaries.toFormValue(question),
    ...QuestionKey.toFormValue(question),
    title: question.question,
    requirement: question.requirement ?? "NotRequired",
  };
}

export const EmptyFormikValues: FormikType = {
  ...QuestionConstraints.EmptyFormikValue,
  ...QuestionVisibility.EmptyFormikValue,
  ...ConfigureLocationBoundaries.EmptyFormValue,
  ...PermissionLevel.EmptyFormikValue,
  title: "",
  type: "FreeText",
  key: "",
  userGeneratedKey: false,
  link: { type: "disabled", url: "", text: "" },
  options: [],
  requirement: "NotRequired",
  birthdateFilter: { type: "off", filters: [] },
  verification: {
    switch: "disabled",
    id: "",
    label: "",
    is_avela_ai_assisted: false,
    is_identity_ai_assisted: false,
    is_new: false,
  },
};

type Grade = { id: string; label: string; value: string | null };
export function toQuestion(
  question: AF.V2.Question,
  values: FormikType,
  grades: Grade[]
): AF.V2.Question {
  const baseQuestion = {
    // Always start with previous version of question since it may contains canonical default values generated by form service
    // See: https://github.com/Avela-Education/apply-form-service/pull/220
    ...(question as any),
    id: question.id,
    question: values.title,
    key: values.key,
    ...toFormVerificationId(values),
    ...toRequirement(values),
    ...toLink(values),
    ...PermissionLevel.fromFormValue(values),
    ...toFilters(values),
    ...toVisibility(values),
  };

  switch (values.type) {
    case "FreeText":
    case "Address":
    case "FileUpload":
    case "Email":
    case "PhoneNumber":
      return {
        ...baseQuestion,
        type: values.type,
      } satisfies AF.V2.Question;

    case "CustomQuestion":
      return {
        ...baseQuestion,
        type: values.type,
        ...toCustomQuestion(values),
      };

    case "Number":
      return {
        ...baseQuestion,
        type: values.type,
        ...toNumberQuestion(values),
      };
    case "Date":
      return {
        ...baseQuestion,
        type: values.type,
        ...DateConstraints.toDateQuestion(values),
      };

    case "Grades":
      return {
        ...baseQuestion,
        type: values.type,
        options: grades.map((grade) => ({
          id: grade.id,
          label: grade.label,
          value: grade.value ?? undefined,
        })),
      };

    case "SingleSelect":
    case "MultiSelect":
      return {
        ...baseQuestion,
        type: values.type,
        ...toOptions("options" in question ? question.options : [], values),
      };

    default: {
      const _exhaustiveCheck: never = values.type;
      return {} as any;
    }
  }
}

export function toVerification(
  values: FormikType
): AF.V2.FormVerification | undefined {
  if (values.verification.switch === "enabled") {
    const verification = {
      id: values.verification.id,
      label: values.verification.label,
      is_avela_ai_assisted: values.verification.is_avela_ai_assisted,
      is_identity_ai_assisted: values.verification.is_identity_ai_assisted,
    };
    return verification;
  }
}

export function toFormVerificationId(
  values: FormikType
): { formVerificationId?: string } | undefined {
  return {
    formVerificationId:
      values.verification.switch === "enabled"
        ? values.verification.id
        : undefined,
  };
}

export function toDeletedVerificationIds(
  values: FormikType
): string[] | undefined {
  return values.deletedVerificationIds;
}

function toRequirement(values: FormikType): {
  requirement: AF.V2.requirement | undefined;
} {
  return {
    requirement: values.requirement === "Required" ? "Required" : undefined,
  };
}

function toLink(values: FormikType): {
  link_url?: string;
  link_text?: string;
} {
  if (values.link.type === "disabled") {
    return { link_text: undefined, link_url: undefined };
  }

  return { link_text: values.link.text, link_url: values.link.url };
}

function toFilters(values: FormikType): Pick<AF.V2.Question, "filters"> {
  switch (values.type) {
    case "Address": {
      return ConfigureLocationBoundaries.toAddressQuestion(values);
    }

    case "Grades": {
      if (values.birthdateFilter.type === "off") {
        return { filters: undefined };
      }

      return {
        filters: values.birthdateFilter.filters.map(
          (
            filter
          ):
            | AF.V2.BirthdateAfterFilter
            | AF.V2.BirthdateBeforeFilter
            | AF.V2.BirthdateBetweenFilter => {
            switch (filter.type) {
              case "BirthdateAfterFilter": {
                return {
                  type: filter.type,
                  config: {
                    startDate: filter.startDate,
                    keys: filter.gradeIds,
                  },
                };
              }
              case "BirthdateBeforeFilter": {
                return {
                  type: filter.type,
                  config: {
                    endDate: filter.endDate,
                    keys: filter.gradeIds,
                  },
                };
              }
              case "BirthdateBetweenFilter": {
                return {
                  type: filter.type,
                  config: {
                    startDate: filter.startDate,
                    endDate: filter.endDate,
                    keys: filter.gradeIds,
                  },
                };
              }
              default: {
                const _exhaustiveCheck: never = filter;
                throw new Error(`Unsupported filter type: ${filter}`);
              }
            }
          }
        ),
      };
    }

    default: {
      return { filters: [] };
    }
  }
}

function toVisibility(values: FormikType): {
  visibility?: AF.V2.Visibility | null;
} {
  return { visibility: QuestionVisibility.fromFormValue(values) };
}

function toOptions(
  options: AF.V2.Option[],
  values: FormikType
): { options: AF.V2.Option[] } {
  if (values.type !== "SingleSelect" && values.type !== "MultiSelect") {
    return { options: [] };
  }

  return {
    options: values.options.map((o): AF.V2.Option => {
      const originalOption = options.find((original) => original.id === o.id);
      const baseOption = {
        ...originalOption,
        id: o.id,
        label: o.label,
        value: o.value,
        isArchived: o.isArchived,
      };

      if (values.type === "MultiSelect") {
        return baseOption;
      }

      switch (o.eligibility?.type) {
        case "disabled":
        case undefined:
          const { notEligibleSchoolIds, ...optionWithoutSchoolIds } = {
            notEligibleSchoolIds: undefined,
            ...baseOption,
          };
          return {
            ...optionWithoutSchoolIds,
            eligibilityFilter: "NotApplicable" as const,
          };
        case "excludes":
          return {
            ...baseOption,
            eligibilityFilter: "NotEligible" as const,
            notEligibleSchoolIds: o.eligibility.schoolIds,
          };
        default: {
          const _exhaustiveCheck: never = o.eligibility;
          throw new Error(`Unsupported eligiblity type: ${o.eligibility}`);
        }
      }
    }),
  };
}

function toNumberQuestion(values: FormikType): {
  constraints?: AF.V2.NumberTypeConstraints;
} {
  if (values.type !== "Number") {
    return { constraints: undefined };
  }

  return {
    constraints: {
      format: values.numberFormat,
      precision: isDecimalNumberFormat(values.numberFormat)
        ? Math.max(Number(values.numberPrecision) || 1, 1)
        : 0,
      ...(values.numberConstraints.switch === "on"
        ? {
            minValue: values.numberConstraints.minValue,
            maxValue: values.numberConstraints.maxValue,
          }
        : { minValue: undefined, maxValue: undefined }),
    },
  };
}

function toCustomQuestion(values: FormikType): {
  customQuestionTypeId: string;
} {
  if (values.type !== "CustomQuestion") {
    return { customQuestionTypeId: "" };
  }

  return {
    customQuestionTypeId: values.customQuestionTypeId ?? "",
  };
}
